import { useState, useEffect, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { cn } from '@utils'
import { InputType } from '@constants'
import { useSessionStore } from '@stores'
import InputButton from './InputButton'
import InputField from '@/components/steps/InputField'
import { updateFontSizes } from '@utils/fontSizing'
import { parseStyleProp } from '@utils/dom'

interface SelectOption {
  value: any
  text: string
  sortVal?: number
  isCurrent?: boolean
}

interface AdditionalField {
  name: string
  type: string
  label?: string
  default?: any
  max?: string
  [key: string]: any
}

interface Input {
  id: string | number
  type: InputType
  formatted_label: string
  input_fields?: Record<string, any>
  button_styles?: any
}

interface ButtonStyles {
  confirm?: {
    label: string
    style: unknown
    style_type?: any
  }
}

interface ChoiceFieldsProps {
  session?: any
  onSubmit?: (input: any, inputData?: Record<string, any>) => void
  onBack?: () => void
  className?: string
}

const ChoiceFields = ({ session: sessionProp, onSubmit, onBack, className }: ChoiceFieldsProps) => {
  const storeSession = useSessionStore(state => state.session)
  // In a popup the live step is popupSession; callers pass it explicitly. The
  // store session is the background step and would yield the wrong options.
  const session = sessionProp || storeSession

  const derived = useMemo(() => {
    if (!session?.valid_inputs?.length) {
      return {
        inputType: null as InputType | null,
        initialSelectedInput: null as any,
        selectOptions: [] as SelectOption[],
        additionalFields: [] as AdditionalField[],
        additionalInputs: [] as Input[],
        initialInputData: {} as Record<string, any>,
      }
    }

    const firstInput = session.valid_inputs[0]
    const newAdditionalFields: AdditionalField[] = []
    const newInputData: Record<string, any> = {}

    if (firstInput.input_fields) {
      for (const [key, field] of Object.entries(firstInput.input_fields)) {
        if (key !== 'billing_interval_days') {
          newAdditionalFields.push({ name: key, ...(field as any) })
        }
        const fieldData = field as any
        if (fieldData.default) {
          newInputData[key] = fieldData.default
        } else if (session.selected_item && key in session.selected_item) {
          newInputData[key] = session.selected_item[key]
        }
      }
    }

    let initialSelectedInput: any = null
    const newSelectOptions: SelectOption[] = []
    const newAdditionalInputs: Input[] = []

    if (session.valid_inputs.length === 1) {
      initialSelectedInput = firstInput
    } else {
      let isBillingIntervalSelection = false
      for (const input of session.valid_inputs) {
        if ([InputType.Radio, InputType.Select, InputType.RadioSelect].includes(input.type)) {
          let sortVal = 0
          let isCurrentSelection = false

          if (session.selected_item && input.input_fields && 'billing_interval_days' in input.input_fields) {
            const field = input.input_fields.billing_interval_days
            isBillingIntervalSelection = true

            if ('billing_interval_days' in newInputData) {
              delete newInputData['billing_interval_days']
            }

            if (field && 'type' in field && 'value' in field && field.type === InputType.Hidden) {
              sortVal = field.value
              if (field.value === null || field.value === session.selected_item.billing_interval_days) {
                if (!initialSelectedInput) {
                  initialSelectedInput = input
                  isCurrentSelection = true
                  sortVal = session.selected_item.billing_interval_days || 0
                }
              }
            }
          }

          newSelectOptions.push({
            value: input,
            text: input.formatted_label,
            sortVal,
            isCurrent: isCurrentSelection,
          })
        } else {
          newAdditionalInputs.push(input)
        }
      }

      if (isBillingIntervalSelection) {
        newSelectOptions.sort((a, b) => (a.sortVal || 0) - (b.sortVal || 0))
      }
    }

    return {
      inputType: firstInput.type as InputType,
      initialSelectedInput,
      selectOptions: newSelectOptions,
      additionalFields: newAdditionalFields,
      additionalInputs: newAdditionalInputs,
      initialInputData: newInputData,
    }
  }, [session])

  const { inputType, selectOptions, additionalInputs } = derived

  const [selectedInput, setSelectedInput] = useState<any>(derived.initialSelectedInput)
  const [inputData, setInputData] = useState<Record<string, any>>(derived.initialInputData)
  const [additionalFields, setAdditionalFields] = useState<AdditionalField[]>(derived.additionalFields)

  const methods = useForm<Record<string, any>>({
    mode: 'onChange',
    defaultValues: derived.initialInputData,
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Re-seeds the form from a new step; paired with methods.reset, which cannot run during render.
    setSelectedInput(derived.initialSelectedInput)
    setInputData(derived.initialInputData)
    setAdditionalFields(derived.additionalFields)
    methods.reset(derived.initialInputData)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `methods` is a new react-hook-form object each render; depending on it would loop.
  }, [derived])

  const buttonLabel = useMemo(() => {
    const bs: any = (session as any)?.button_styles
    if (bs?.confirm?.label) {
      return updateFontSizes(bs.confirm.label)
    }
    return additionalFields.length ? 'Save' : 'Continue'
  }, [session, additionalFields.length])

  const buttonStyles: ButtonStyles | null = (session as any)?.button_styles || null

  const isRadioGroup = inputType === InputType.Radio
  const isSelect = inputType === InputType.Select
  const isRadioOrSelect = inputType === InputType.RadioSelect
  const canUndo = !!session && (session.can_undo || session.is_step_preview)

  const updateData = (field: string, value: any) => {
    setInputData(prev => ({ ...prev, [field]: value }))
    methods.setValue(field, value, { shouldValidate: true, shouldDirty: true })
  }

  const submitForm = () => {
    if (!selectedInput) {
      return
    }
    const formValues = methods.getValues()
    onSubmit?.(selectedInput, { ...inputData, ...formValues })
  }

  const radioGroupValueChanged = (data: any) => {
    if (data?.input_fields && 'billing_interval_days' in data.input_fields && 'next_bill_date' in data.input_fields) {
      const billingIntervalDays = data.input_fields.billing_interval_days.value
      const updatedBillDate = new Date()
      updatedBillDate.setDate(updatedBillDate.getDate() + billingIntervalDays)
      const yyyy = updatedBillDate.getFullYear()
      const mm = String(updatedBillDate.getMonth() + 1).padStart(2, '0')
      const dd = String(updatedBillDate.getDate()).padStart(2, '0')
      const formattedDate = `${yyyy}-${mm}-${dd}`
      updateData('next_bill_date', formattedDate)
      setAdditionalFields(prev =>
        prev.map(field => (field.name === 'next_bill_date' ? { ...field, max: formattedDate } : field))
      )
    }
  }

  if (!session?.valid_inputs?.length) {
    return null
  }

  return (
    <section className={cn('flex flex-col items-center gap-3 mb-3 w-full', className)}>
      <div className="w-full mx-auto" id="selected_item">
        <div className="flex flex-wrap justify-center w-full">
          <div className="w-full lg:w-5/6">
            {inputType && (
              <div className={cn('form-fields flex flex-wrap md:justify-center', { 'pt-4': session.is_legacy })}>
                {selectOptions?.length > 0 && (
                  <div className="w-full md:w-5/6">
                    {(isRadioGroup || isRadioOrSelect) && (
                      <div className={cn('form-group', { 'hidden md:block': isRadioOrSelect })}>
                        <div id="radio-group">
                          {selectOptions.map((option, index) => (
                            <label
                              key={option.value.id}
                              className={cn('choice-card', { selected: option.value === selectedInput })}
                              htmlFor={`option-${option.value.id}`}>
                              <input
                                className="form-check-input"
                                type="radio"
                                id={`option-${option.value.id}`}
                                name="radio-group"
                                value={index}
                                checked={option.value === selectedInput}
                                onChange={() => {
                                  setSelectedInput(option.value)
                                  radioGroupValueChanged(option.value)
                                }}
                              />
                              <span className="form-check-label">
                                <span dangerouslySetInnerHTML={{ __html: option.text }} />
                                {option.isCurrent && <span> (current)</span>}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {(isSelect || isRadioOrSelect) && (
                      <div className={cn('form-group', { 'md:hidden': isRadioOrSelect })}>
                        <select
                          id="select-group"
                          className="form-select"
                          value={selectedInput?.id != null ? String(selectedInput.id) : ''}
                          onChange={e => {
                            const selectedOption = selectOptions.find(
                              option => String(option.value.id) === e.target.value
                            )
                            if (selectedOption) {
                              setSelectedInput(selectedOption.value)
                            }
                          }}>
                          <option value="">--- Please Select ---</option>
                          {selectOptions.map(option => (
                            <option key={option.value.id} value={String(option.value.id)}>
                              {option.text}
                              {option.isCurrent ? ' (current)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {additionalFields.length > 0 && (
                  <FormProvider {...methods}>
                    {additionalFields.map((field, index) => (
                      <div key={`input-data-field-${index}`} className="w-full md:w-5/12">
                        <InputField field={field as any} item={session?.selected_item} />
                      </div>
                    ))}
                  </FormProvider>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-center w-full">
          <div className="w-full lg:w-5/6">
            <div className="choice-fields-btns">
              {buttonLabel && (
                <button
                  type="button"
                  className="btn-base input-choice-btn"
                  style={parseStyleProp(buttonStyles?.confirm?.style)}
                  onClick={submitForm}
                  disabled={!selectedInput || (additionalFields.length > 0 && !methods.formState.isValid)}>
                  <span dangerouslySetInnerHTML={{ __html: buttonLabel }} />
                </button>
              )}

              {additionalInputs.map(input => (
                <InputButton key={`choice-${input.id}`} input={input} onClick={() => onSubmit?.(input)} />
              ))}

              {canUndo && (
                <button type="button" className="btn-base back-btn" onClick={() => onBack?.()}>
                  Back
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ChoiceFields
